import React, { useState } from 'react';
import { Radar, Loader2 } from 'lucide-react';
import { fiatService } from '../../../../services/api';
import { toErrorMessage } from '../../../../utils/safeStr';
import { SOF, STYLES } from '../copy';
import { SOF_EXAMPLE_JSON, SOF_DEFAULT_ACCOUNT } from '../examples';
import type { OffRampToolPanelProps } from '../types';
import { TierBadge } from '../shared/TierBadge';
import { ResultEmptyHint } from '../shared/ResultEmptyHint';

function triggeredLabel(triggered: boolean | null | undefined): string {
    if (triggered === true) return SOF.triggered;
    if (triggered === false) return SOF.clear;
    return SOF.notTested;
}

function signalLabel(triggered: boolean | null | undefined): string {
    if (triggered === true) return 'HIT';
    if (triggered === false) return 'CLEAR';
    return SOF.notTested;
}

export function SofEddPanel({ className, emphasizeExample, onComplete }: OffRampToolPanelProps) {
    const [eventsText, setEventsText] = useState('');
    const [sofAccount, setSofAccount] = useState(SOF_DEFAULT_ACCOUNT);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const runSof = async (text: string) => {
        let events: any[];
        try {
            events = JSON.parse(text || '[]');
            if (!Array.isArray(events)) throw new Error('Expected a JSON array of legs.');
        } catch (e) {
            setError(`Invalid JSON: ${toErrorMessage(e)}`);
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const res = await fiatService.sofEdd({
                events,
                account_ref: sofAccount || undefined,
            });
            setResult(res);
            onComplete?.();
        } catch (e) {
            setError(toErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const tryExample = () => {
        setEventsText(SOF_EXAMPLE_JSON);
        setSofAccount(SOF_DEFAULT_ACCOUNT);
        void runSof(SOF_EXAMPLE_JSON);
    };

    return (
        <div className={`${STYLES.card} ${className ?? ''}`}>
            <ResultEmptyHint>{SOF.empty}</ResultEmptyHint>
            <div>
                <label className={STYLES.label}>Account ref</label>
                <input
                    className={STYLES.input}
                    value={sofAccount}
                    onChange={(e) => setSofAccount(e.target.value)}
                />
            </div>
            <textarea
                className={`${STYLES.input} font-mono text-[11px] h-36 resize-y`}
                placeholder="Inflow-heavy statement legs JSON…"
                value={eventsText}
                onChange={(e) => setEventsText(e.target.value)}
                aria-label="SoF statement legs JSON"
            />
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btn : STYLES.btnSecondary}
                    onClick={tryExample}
                    disabled={loading}
                >
                    {loading && emphasizeExample ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Radar className="w-4 h-4" />
                    )}
                    {SOF.tryExample}
                </button>
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btnSecondary : STYLES.btn}
                    onClick={() => void runSof(eventsText)}
                    disabled={loading}
                >
                    {loading && !emphasizeExample ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Radar className="w-4 h-4" />
                    )}
                    {SOF.run}
                </button>
                {error && <span className="text-xs text-red-500">{error}</span>}
            </div>
            {result && (
                <div className="border border-border rounded-md p-4 bg-secondary/30 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <TierBadge determinism={result.determinism} />
                        <span className="text-sm font-semibold text-foreground">
                            {triggeredLabel(result.triggered)}
                        </span>
                        {Array.isArray(result.reasons) && result.reasons.length > 0 && (
                            <span className="text-[11px] text-muted-foreground">
                                reasons: {result.reasons.join(', ')}
                            </span>
                        )}
                    </div>
                    {Array.isArray(result.signals) &&
                        result.signals.map((s: any) => (
                            <div key={s.key} className="text-xs flex items-start gap-2">
                                <span className="font-mono text-[10px] w-36 shrink-0 text-muted-foreground">
                                    {s.key}
                                </span>
                                <span>
                                    {signalLabel(s.triggered)}
                                    {' — '}
                                    {s.detail}
                                </span>
                            </div>
                        ))}
                    {Array.isArray(result.matched_counterparties) &&
                        result.matched_counterparties.length > 0 && (
                            <div className="text-[11px] text-muted-foreground pt-1">
                                Matched:{' '}
                                {result.matched_counterparties
                                    .map(
                                        (m: any) =>
                                            `${m.counterparty}→${m.display_name || m.match || 'hit'}`,
                                    )
                                    .join('; ')}
                            </div>
                        )}
                </div>
            )}
        </div>
    );
}
